import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, UserPlus } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

const LS_KEY = "fliboe_signup_alerts_endpoint";

/**
 * Admin-only toggle: subscribes the current browser to receive a web push
 * notification on every new Fliboe signup (email + Google OAuth).
 */
export default function SignupAlertsToggle() {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState(null);
  const [perm, setPerm] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  useEffect(() => {
    if (!pushSupported()) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub && localStorage.getItem(LS_KEY) === sub.endpoint) {
          setSubscribed(true);
        }
        const { data } = await api.get("/admin/notify-signups/status", { headers: authHeaders() });
        setDevices(data?.subscribed_devices ?? 0);
      } catch (_) {}
    })();
  }, []);

  if (!pushSupported()) {
    return (
      <div className="text-xs text-slate-500" data-testid="signup-alerts-unsupported">
        Push notifications aren't supported in this browser. Try Chrome on Android or any modern desktop browser, or install Fliboe as a PWA.
      </div>
    );
  }

  const subscribe = async () => {
    setBusy(true);
    try {
      let permission = perm;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
        setPerm(permission);
      }
      if (permission !== "granted") { setBusy(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data: vapid } = await api.get("/push/vapid-public-key");
      // Reuse an existing browser-wide push subscription if present, else create one.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
        });
      }
      const json = sub.toJSON();
      await api.post(
        "/admin/notify-signups/subscribe",
        { endpoint: json.endpoint, keys: json.keys },
        { headers: authHeaders() },
      );
      localStorage.setItem(LS_KEY, json.endpoint);
      setSubscribed(true);
      const { data } = await api.get("/admin/notify-signups/status", { headers: authHeaders() });
      setDevices(data?.subscribed_devices ?? 0);
    } catch (e) {
      console.warn("[signup-alerts] subscribe failed", e);
    }
    setBusy(false);
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post(
          "/admin/notify-signups/unsubscribe",
          { endpoint: sub.endpoint },
          { headers: authHeaders() },
        ).catch(() => {});
      }
      localStorage.removeItem(LS_KEY);
      setSubscribed(false);
      const { data } = await api.get("/admin/notify-signups/status", { headers: authHeaders() });
      setDevices(data?.subscribed_devices ?? 0);
    } catch (_) {}
    setBusy(false);
  };

  return (
    <section className="mt-10" data-testid="signup-alerts-card">
      <h2 className="fl-display text-2xl font-bold mb-4 flex items-center gap-2">
        <UserPlus className="w-5 h-5 text-emerald-500" /> Signup alerts
      </h2>
      <div className="fl-card p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-white font-medium">Ping me when someone signs up</div>
          <div className="text-xs text-slate-400 mt-1">
            We'll send a web push notification to this device on every new Fliboe signup (email + Google).
            {devices !== null && (
              <> · <span className="text-emerald-500" data-testid="signup-alerts-device-count">{devices}</span> device{devices === 1 ? "" : "s"} subscribed</>
            )}
          </div>
        </div>
        {perm === "denied" ? (
          <button
            disabled
            className="fl-btn ghost opacity-60 cursor-not-allowed"
            title="Notifications are blocked for this site in your browser settings."
            data-testid="signup-alerts-blocked"
          >
            <BellOff className="w-4 h-4" /> Blocked in browser
          </button>
        ) : subscribed ? (
          <button
            onClick={unsubscribe}
            disabled={busy}
            className="fl-btn ghost"
            data-testid="signup-alerts-unsubscribe"
          >
            <BellRing className="w-4 h-4 text-emerald-500" />
            {busy ? "Disabling…" : "Alerts on for this device"}
          </button>
        ) : (
          <button
            onClick={subscribe}
            disabled={busy}
            className="fl-btn"
            data-testid="signup-alerts-subscribe"
          >
            <Bell className="w-4 h-4" />
            {busy ? "Enabling…" : "Enable signup alerts"}
          </button>
        )}
      </div>
    </section>
  );
}
